/**
 * Field-level validation schemas.
 *
 * Reusable Zod schemas for common form fields.
 * All schemas are SSR-safe and can be used in server actions or client validation.
 *
 * @module schemas/field-validators
 */

import { nanpPhonePattern, nanpPostalCodePattern } from "@homeinstead/locale";
import { z } from "zod";

// ============================================================================
// Email
// ============================================================================

/**
 * Email validation schema.
 *
 * Required runs before format so an unanswered field surfaces
 * "Email is required" rather than "Invalid email address".
 */
export const emailSchema = z
  .string({ error: "Email is required" })
  .min(1, { error: "Email is required" })
  .email({ error: "Invalid email address" });

// ============================================================================
// Phone
// ============================================================================

/**
 * Phone number pattern for US and Canadian numbers.
 * Matches formats like: (555) 555-5555, 555-555-5555, 555.555.5555, 5555555555
 *
 * Ensures matching parentheses: either both or neither, not mismatched.
 *
 * Re-exported from @homeinstead/locale for convenience.
 */
export const phonePattern = nanpPhonePattern;

/**
 * Phone validation schema for US and Canadian numbers.
 */
export const phoneSchema = z
  .string({ error: "Phone number is required" })
  .min(1, { error: "Phone number is required" })
  .regex(nanpPhonePattern, {
    error: "Phone number must be a valid US or Canadian number",
  });

// ============================================================================
// Names
// ============================================================================

/**
 * First name validation schema.
 */
export const firstNameSchema = z
  .string({ error: "First name is required" })
  .min(1, { error: "First name is required" })
  .min(2, { error: "First name must be at least 2 characters" });

/**
 * Last name validation schema.
 */
export const lastNameSchema = z
  .string({ error: "Last name is required" })
  .min(1, { error: "Last name is required" })
  .min(2, { error: "Last name must be at least 2 characters" });

// ============================================================================
// Postal Code
// ============================================================================

/**
 * Postal code regex for US ZIP codes and Canadian postal codes.
 *
 * US formats:
 * - 5-digit: 12345
 * - ZIP+4: 12345-6789
 * - 9-digit: 123456789
 *
 * Canadian formats:
 * - With space: K1A 0A6
 * - Without space: K1A0A6
 * - Case insensitive
 */
export const postalCodePattern = nanpPostalCodePattern;

/**
 * Postal code validation schema for US and Canadian postal codes.
 */
export const postalCodeSchema = z
  .string({ error: "Postal code is required" })
  .min(1, { error: "Postal code is required" })
  .regex(postalCodePattern, {
    error: "Postal code must be a valid US ZIP or Canadian postal code",
  });
